if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      this.mql = window.matchMedia('(min-width: 750px)');

      if (!this.elements.viewer) return;

      if (this.elements.thumbnails) {
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      /* Filter product media on mobile */
      this.setupVariantMediaFiltering();
    }

    onSlideChanged(event) {
      if (!this.elements.thumbnails) return;

      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
      );

      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(
        `[data-media-id="${mediaId}"]`
      );

      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

          if (activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: 'smooth'
          });
        }

        if (
          !this.elements.thumbnails ||
          this.dataset.desktopLayout === 'stacked'
        ) {
          activeMedia.scrollIntoView({
            behavior: 'smooth'
          });
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${mediaId}"]`
      );

      this.setActiveThumbnail(activeThumbnail);

      if (activeThumbnail) {
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails
        .querySelectorAll('button')
        .forEach((element) => element.removeAttribute('aria-current'));

      thumbnail.querySelector('button')?.setAttribute('aria-current', true);

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({
        left: thumbnail.offsetLeft,
        behavior: 'smooth'
      });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector(
        '.product__modal-opener--image img'
      );

      if (!image) return;

      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);

        this.elements.liveRegion.innerHTML =
          window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );

        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };

      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();

      const deferredMedia = activeItem.querySelector('.deferred-media');

      if (deferredMedia) {
        deferredMedia.loadContent(false);
      }
    }

    preventStickyHeader() {
      this.stickyHeader =
        this.stickyHeader || document.querySelector('sticky-header');

      if (!this.stickyHeader) return;

      this.stickyHeader.dispatchEvent(
        new Event('preventHeaderReveal')
      );
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;

      this.elements.viewer.slider.setAttribute(
        'role',
        'presentation'
      );

      this.elements.viewer.sliderItems.forEach((slide) =>
        slide.setAttribute('role', 'presentation')
      );
    }

    /* =========================================
       VARIANT MEDIA FILTER
       ========================================= */

    setupVariantMediaFiltering() {
      const runFilter = () => {
        this.filterVariantMedia();
      };

      /* Initial filter */
      setTimeout(runFilter, 100);

      /* Shopify variant picker changes */
      document.addEventListener('change', (event) => {
        const target = event.target;

        if (
          target.matches('input[type="radio"]') ||
          target.matches('select')
        ) {
          setTimeout(runFilter, 50);
        }
      });

      /*
       * Shopify sometimes updates the selected variant
       * without immediately firing a normal change event.
       */
      this.variantObserver = new MutationObserver(() => {
        if (window.innerWidth <= 749) {
          setTimeout(runFilter, 50);
        }
      });

      this.variantObserver.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['checked', 'selected']
      });

      window.addEventListener('resize', runFilter);
    }

    filterVariantMedia() {
      /* Only apply this behavior on mobile. Desktop keeps every media item
         available and uses the separate hover image for front/back. */
      if (window.innerWidth > 749) {
        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((media) => {
            media.removeAttribute('hidden');
            media.style.removeProperty('order');
          });

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((thumbnail) => {
              thumbnail.removeAttribute('hidden');
            });
        }

        if (typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
        return;
      }

      const selectedColor = this.getSelectedColor();
      if (!selectedColor) return;

      const normalizedColor = selectedColor.trim().toLowerCase();
      const mediaItems = Array.from(
        this.elements.viewer.querySelectorAll('[data-media-id]')
      );

      const visibleMedia = [];

      mediaItems.forEach((media) => {
        /* Use only the actual/main image in each slide. The additional
           .ess-product-back-image exists only for desktop hover. */
        const image = media.querySelector('.ess-product-primary-image') || media.querySelector('img');

        if (!image) {
          media.setAttribute('hidden', '');
          return;
        }

        const altText = (image.getAttribute('alt') || '').toLowerCase();
        const matchesColor = altText.includes(normalizedColor);

        if (matchesColor) {
          media.removeAttribute('hidden');
          visibleMedia.push(media);
        } else {
          media.setAttribute('hidden', '');
        }
      });

      /* Keep the currently selected variant's featured image first. Shopify's
         variant picker prepends that media before this runs, so DOM order here
         mirrors the catalog behavior: front first, matching back second. */
      visibleMedia.forEach((media, index) => {
        media.style.order = String(index);
      });
      mediaItems.filter((media) => !visibleMedia.includes(media)).forEach((media, index) => {
        media.style.order = String(100 + index);
      });

      /* Filter thumbnails to the same variant media (the strip is hidden by
         design, but keeping its state accurate avoids Shopify gallery drift). */
      if (this.elements.thumbnails) {
        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((thumbnail) => {
            const mediaId = thumbnail.dataset.target;
            const matchingMedia = this.elements.viewer.querySelector(
              `[data-media-id="${mediaId}"]`
            );

            if (matchingMedia && !matchingMedia.hasAttribute('hidden')) {
              thumbnail.removeAttribute('hidden');
            } else {
              thumbnail.setAttribute('hidden', '');
            }
          });
      }

      if (!visibleMedia.length) return;

      /* Always return to the front image after a color change. This also fixes
         cases where changing variants left the mobile slider parked on 2/2. */
      const firstVisible = visibleMedia[0];

      this.elements.viewer
        .querySelectorAll('[data-media-id]')
        .forEach((media) => media.classList.remove('is-active'));
      firstVisible.classList.add('is-active');

      /* Hidden slides change the slider geometry. Dawn caches those widths, so
         recalculate after every variant filter or the arrow can remain stuck at
         1/2 even though the second image exists. */
      window.requestAnimationFrame(() => {
        if (typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }

        const slider = this.elements.viewer.slider;
        if (slider) {
          slider.scrollTo({ left: firstVisible.offsetLeft, behavior: 'auto' });
        }

        window.requestAnimationFrame(() => {
          if (typeof this.elements.viewer.resetPages === 'function') {
            this.elements.viewer.resetPages();
          }
        });
      });
    }

    getSelectedColor() {
      /*
       * Dawn/Shopify normally uses an input named "Color".
       */
      const colorInput = document.querySelector(
        'input[name="Color"]:checked, ' +
        'input[name="color"]:checked, ' +
        'input[name*="Color"]:checked, ' +
        'input[name*="color"]:checked'
      );

      if (colorInput && colorInput.value) {
        return colorInput.value;
      }

      /*
       * Fallback for select-based variant pickers.
       */
      const colorSelect = document.querySelector(
        'select[name="options[Color]"], ' +
        'select[name="options[color]"], ' +
        'select[name*="Color"], ' +
        'select[name*="color"]'
      );

      if (colorSelect && colorSelect.value) {
        return colorSelect.value;
      }

      return null;
    }
  });
}